import './Balance.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TonWeb from 'tonweb';
import { useRoute } from 'react-router5';

import {
  Button,
  Header,
  Placeholder,
  PullToRefresh,
  ScrollView,
  SegmentedControl,
  Tabs,
  TabsItem,
} from '$uikit';
import { balanceActions, balanceSelector } from '$store/balance';
import { Loader, Tooltip } from '$shared/components';
import { contentPrioritySelector, mainSelector } from '$store/main';
import { classNames, format, formatCurrency } from '$utils';
import { useCurrentUserInfo, useTranslator, useWikiLink } from '$hooks';
import { authSelector } from '$store/auth';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import { showModal } from '$navigation/helper';
import { BalanceOperationType, CurrencyEnum } from '$store/models';
import { ContentPriorityType } from '$store/main/interface';
import { IcNewTab28 } from '$assets';

export const Balance: FC = () => {
  const {
    isLoading,
    isFailed,
    isGold,
    operations,
    withdrawals,
    holdBalance,
    tonHoldBalance,
    holdDays,
  } = useSelector(balanceSelector);
  const { balance, eurBalance, placeBalance, isCryptoEnabled } =
    useSelector(mainSelector);
  const dispatch = useDispatch();
  const user = useCurrentUserInfo();
  const { groups } = useSelector(mainSelector);
  const t = useTranslator();
  const { isLogged } = useSelector(authSelector);
  const [tab, setTab] = useState('operations');
  const { route } = useRoute();
  const contentPriority = useSelector(contentPrioritySelector);
  const isAdultPriority = contentPriority === ContentPriorityType.ADULT;
  const currency =
    route.params.currency ||
    (user.verified === 2 || isAdultPriority ? CurrencyEnum.EUR : CurrencyEnum.TON);

  const balances = {
    [CurrencyEnum.TON]: balance,
    [CurrencyEnum.EUR]: eurBalance,
    [CurrencyEnum.PLACE]: placeBalance,
  };

  const pendingBalanceWikiLink = useWikiLink(
    '/for-creators/balance-and-payments/pending-balance',
  );

  const pendingTooltipText =
    holdDays === 21
      ? t('balance_pending_tooltip_new_account', {
          defaultValue:
            'For new accounts, funds stay on hold for 21 days. After the hold period will be reduced to 7 days.',
        })
      : t('balance_pending_tooltip');

  useEffect(() => {
    dispatch(balanceActions.loadBalance());
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(balanceActions.loadBalance());
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(balanceActions.loadBalance());
  }, []);

  const handleTopUp = useCallback(() => {
    if (currency === CurrencyEnum.TON) {
      dispatch(
        balanceActions.setTopUpTonActive({
          topUpActive: true,
          requireTopUpAmount: 0,
        }),
      );
    } else {
      dispatch(
        balanceActions.setTopUpActive({
          topUpActive: true,
        }),
      );
    }
  }, [currency]);

  const handleWithdraw = useCallback(() => {
    if (currency === CurrencyEnum.EUR) {
      showModal('convert_eur');
    } else {
      showModal('withdraw');
    }
  }, [currency]);

  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
  }, []);

  const handleChangeCurrency = useCallback(
    (newCurrency: string) => {
      const params: any = { ...route.params };
      params.currency = newCurrency;
      showModal('balance', true, params);
      dispatch(balanceActions.loadBalance());
    },
    [route.params],
  );

  function renderTabContent() {
    if (tab === 'operations' || currency === CurrencyEnum.EUR) {
      return (
        <>
          {operations.length > 0 && (
            <div className="Balance__history__tip">{t('balance_history_tip')}</div>
          )}
          <div className="Balance__history">{renderHistory()}</div>
        </>
      );
    } else if (tab === 'withdrawals') {
      return (
        <>
          {withdrawals.length > 0 && (
            <div className="Balance__history__tip">{t('balance_history_tip')}</div>
          )}
          <div className="Balance__history">{renderWithdrawals()}</div>
        </>
      );
    }
  }

  function renderWithdrawals() {
    if (!withdrawals.length) {
      return <Placeholder>{t('balance_withdrawals_empty')}</Placeholder>;
    }

    return withdrawals.map((w) => {
      let addressReadable = w.address;
      try {
        addressReadable = new TonWeb.utils.Address(w.address).toString(
          true,
          false,
          false,
          false,
        );
      } catch (e) {}
      const address = `${addressReadable.substr(0, 7)}...${addressReadable.substr(-7)}`;
      let status = t('balance_withdrawal_status_' + w.status);

      return (
        <div
          key={w.id}
          className={classNames({
            Balance__history__item: true,
            withdrawal: true,
            [w.status]: true,
          })}
        >
          <div className="Balance__history__item__cont">
            <div className="Balance__history__item__title">{status}</div>
            <div className="Balance__history__item__caption">{address}</div>
          </div>
          <div className="Balance__history__item__aside">
            <div className="Balance__history__item__amount">
              - {formatCurrency(w.amount, CurrencyEnum.TON)}
            </div>
            <div className="Balance__history__item__caption">
              {format(w.createdAt * 1000, 'dd MMM HH:mm')}
            </div>
          </div>
        </div>
      );
    });
  }

  function renderHistory() {
    if (!operations.length) {
      return <Placeholder>{t('balance_history_empty')}</Placeholder>;
    }

    return operations.map((o) => {
      let title = '',
        caption = '',
        style = '';
      if (o.operation === 'withdraw') {
        title = t('balance_operation_withdraw_title');
        style = 'withdraw';
        caption = t('balance_operation_withdraw_caption', {
          wallet: `${o.extra.substr(0, 7)}...${o.extra.substr(-7)}`,
        });
      } else if (o.operation === 'stake_profit') {
        title = t('balance_operation_profit_title');
        caption = t('balance_operation_staking_caption');
        style = 'refill';
      } else if (o.operation === 'profit') {
        title = t('balance_operation_profit_title');
        style = 'refill';
        const group = groups[+o.extra];
        if (group) {
          caption = t('balance_operation_profit_caption_group', {
            groupName: group.name,
          });
        } else if (!+o.extra) {
          caption = t('balance_operation_profit_caption_profile');
        }
      } else if (o.operation === 'referral_profit') {
        title = t('balance_operation_profit_title');
        style = 'refill';
        caption = t('balance_operation_referral_profit_caption_group');
      } else if (o.operation === 'referral_profit_subscription') {
        title = t('balance_operation_referral_profit_title');
        style = 'refill';
        caption = t('balance_operation_referral_profit_subscription_caption');
      } else if (o.operation === 'referral_profit_message') {
        title = t('balance_operation_referral_profit_title');
        style = 'refill';
        caption = t('balance_operation_referral_profit_message_caption');
      } else if (o.operation === 'referral_profit_tips') {
        title = t('balance_operation_referral_profit_title');
        style = 'refill';
        caption = t('balance_operation_referral_profit_tips_caption');
      } else if (o.operation === 'referral_profit_sponsor_subscription') {
        title = t('balance_operation_referral_profit_title');
        style = 'refill';
        caption = t('balance_operation_referral_profit_sponsor_subscription_caption');
      } else if (
        o.operation === 'top_up' ||
        o.operation === BalanceOperationType.BuyTon
      ) {
        title = t('balance_operation_top_up_title');
        style = 'refill';
        if (o.currency === CurrencyEnum.TON && o.operation === 'top_up') {
          caption = t('balance_operation_top_up_caption', {
            wallet: `${o.extra.substr(0, 7)}...${o.extra.substr(-7)}`,
          });
        }
      } else if (o.operation === 'sold_message') {
        title = t('balance_operation_sold_message_title');
        style = 'refill';
      } else if (o.operation === 'sold_bundle') {
        title = t('balance_operation_sold_bundle_title');
        style = 'refill';
      } else if (o.operation === 'buy_bundle') {
        title = t('balance_operation_buy_bundle_title');
        style = 'withdraw';
      } else if (o.operation === 'referral_profit_bundle') {
        title = t('balance_operation_referral_profit_title');
        caption = t('balance_operation_referral_profit_bundle_caption');
        style = 'refill';
      } else if (o.operation === 'buy_message') {
        title = t('balance_operation_buy_message_title');
        style = 'withdraw';
      } else if (o.operation === 'contest') {
        title = t('balance_operation_contest_title');
        style = 'refill';
      } else if (o.operation === 'subscription_profit') {
        title = t('balance_operation_subscription_profit_title');
        caption = t('balance_operation_subscription_profit_caption');
        style = 'refill';
      } else if (o.operation === 'receive_tips') {
        title = t('balance_operation_receive_tips_title');
        style = 'refill';
      } else if (o.operation === 'send_tips') {
        title = t('balance_operation_send_tips_title');
        style = 'withdraw';
      } else if (o.operation === 'ads_refill') {
        title = t('balance_operation_ads_refill_title');
        style = 'withdraw';
      } else if (o.operation === 'ads_withdraw') {
        title = t('balance_operation_ads_withdraw_title');
        style = 'refill';
      } else if (o.operation === 'profit_ads') {
        title = t('balance_operation_ads_profit_title');
        style = 'refill';
      } else if (o.operation === 'exchange_to_tons') {
        title = t('balance_operation_exchange_to_tons_title');
        style = 'withdraw';
      } else if (o.operation === 'exchange_from_eur') {
        title = t('balance_operation_exchange_from_eur_title');
        style = 'refill';
      } else if (o.operation === 'buy_sponsor_subscription') {
        title = t('balance_operation_buy_sponsor_subscription_title');
        style = 'withdraw';
      } else if (o.operation === 'buy_subscription_auto_charge') {
        title = t('balance_operation_buy_subscription_auto_charge_title');
        style = 'withdraw';
      } else if (o.operation === 'buy_sponsor_subscription_auto_charge') {
        title = t('balance_operation_buy_sponsor_subscription_auto_charge_title');
        style = 'withdraw';
      } else if (
        o.operation === 'buy_subscription' ||
        o.operation === 'buy_trial_subscription' ||
        o.operation === 'buy_candy_subscription'
      ) {
        title = t('balance_operation_buy_subscription_title');
        style = 'withdraw';
      } else if (o.operation === 'refund_to_balance') {
        title = t('balance_operation_refund_title');
        caption = t(`balance_operation_refund_to_balance_${o.extra}_title`);
        style = 'refill';
      } else if (o.operation === 'refund') {
        title = t('balance_operation_refund_title');
        caption = t(`balance_operation_refund_${o.extra}_title`);
        style = 'withdraw';
      } else if (o.operation === 'top_up_eur') {
        title = t('balance_operation_top_up_eur_title');
        style = 'withdraw';
      } else if (o.operation === 'top_up_manual') {
        title = t('balance_operation_top_up_manual_title');
        style = 'refill';
      } else if (o.operation === 'top_up_welcome_bonus') {
        title = t('balance_operation_top_up_welcome_bonus_title');
        style = 'refill';
      } else if (o.operation === 'heyfans_payout') {
        title = t('balance_operation_heyfans_payout_title');
        style = 'refill';
      } else if (o.operation === 'cashback') {
        title = t('balance_operation_cashback_title');
        style = 'refill';
      } else if (o.operation === 'adjustment_negative') {
        title = t('balance_operation_adjustment_negative_title');
        style = 'withdraw';
      } else if (o.operation === 'battle_donate') {
        title = t('balance_operation_battle_donate_title');
        style = 'withdraw';
      } else if (o.operation === 'battle_reward') {
        title = t('balance_operation_battle_reward_title');
        style = 'refill';
      } else if (o.operation === 'sell_telegram_post') {
        title = t('balance_operation_sell_telegram_post_title');
        style = 'refill';
      } else if (o.operation === 'task_reward') {
        title = t('balance_operation_task_reward_title');
        style = 'refill';
      } else if (o.operation === 'cancel_funds') {
        title = t('balance_operation_cancel_funds_title');
        caption = t(`balance_operation_${o.extra}_title`);
        style = 'withdraw';
      } else if (o.operation === 'offer_share') {
        title = t('balance_operation_offer_share_title');
        style = 'withdraw';
      } else if (o.operation === 'mine_spend' && o.extra === 'fee_reduction') {
        title = t('place_spend_type_fee_reduction_title');
        style = 'withdraw';
      } else if (o.operation === 'mine_spend' && o.extra === 'upgrade_squad') {
        title = 'Upgrade squad';
        style = 'withdraw';
      } else if (o.operation === BalanceOperationType.MineExploreCard) {
        title = 'Mine Explore Card';
        style = 'refill';
      }

      return (
        <div
          key={o.id}
          className={classNames({
            Balance__history__item: true,
            [style]: true,
          })}
        >
          <div className="Balance__history__item__cont">
            <div className="Balance__history__item__title">{title}</div>
            <div className="Balance__history__item__caption">{caption}</div>
          </div>
          <div className="Balance__history__item__aside">
            <div className="Balance__history__item__amount">
              {style === 'refill' ? '+' : '-'}{' '}
              {formatCurrency(o.amount, o.currency.toLowerCase(), { symbol: false })}{' '}
              {o.currency.toUpperCase()}
            </div>
            <div className="Balance__history__item__caption">
              {format(o.createdAt * 1000, 'dd MMM HH:mm')}
            </div>
          </div>
        </div>
      );
    });
  }

  function renderContent() {
    if (isLoading) {
      return <Loader isWrapped />;
    }

    if (isFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={handleRetry}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <div className="Balance__wrap">
        <div className="Balance__info">
          <div className="Balance__info_amount_label">{t('balance_available')}</div>
          <div className="Balance__info_amount">
            {formatCurrency(balances[currency], currency)}
            {!!+holdBalance && currency === CurrencyEnum.EUR && (
              <div className="Balance__info_amount_hold">
                <Tooltip
                  iconLight
                  title={t('balance_pending_title')}
                  text={pendingTooltipText}
                  action={
                    <Button
                      stretched
                      iconRight={<IcNewTab28 />}
                      href={pendingBalanceWikiLink}
                      target="_blank"
                    >
                      {t('read_more')}
                    </Button>
                  }
                >
                  {t('balance_pending')} {formatCurrency(holdBalance, CurrencyEnum.EUR)}
                </Tooltip>
              </div>
            )}
            {!!+tonHoldBalance && currency === CurrencyEnum.TON && (
              <div className="Balance__info_amount_hold">
                <Tooltip
                  iconLight
                  title={t('balance_pending_title')}
                  text={pendingTooltipText}
                  action={
                    <Button
                      stretched
                      iconRight={<IcNewTab28 />}
                      href={pendingBalanceWikiLink}
                      target="_blank"
                    >
                      {t('read_more')}
                    </Button>
                  }
                >
                  {t('balance_pending')}{' '}
                  {formatCurrency(tonHoldBalance, CurrencyEnum.TON)}
                </Tooltip>
              </div>
            )}
          </div>
          <div className="Balance__buttons">
            {(currency === CurrencyEnum.TON ||
              (user.verified !== 2 && !isGold && currency !== CurrencyEnum.PLACE)) && (
              <Button onClick={handleTopUp}>{t('balance_top_up_button')}</Button>
            )}
            {currency !== CurrencyEnum.PLACE &&
              (user.verified > 0 || currency === CurrencyEnum.TON || isGold) && (
                <Button onClick={handleWithdraw} mode="secondary">
                  {t('balance_withdraw_button')}
                </Button>
              )}
            {currency === CurrencyEnum.PLACE && user.verified === 2 && (
              <Button onClick={() => showModal('place_spend')}>
                {t('balance_place_spend_button')}
              </Button>
            )}
          </div>
        </div>
        {/*{currency === CurrencyEnum.TON && (*/}
        {/*  <div className="Balance__staking">*/}
        {/*    <a href="https://wiki.ton.place/steiking" target="_blank" rel="noreferrer">*/}
        {/*      <InfoBlock type="info" arrow>*/}
        {/*        {t('balance_staking')}*/}
        {/*      </InfoBlock>*/}
        {/*    </a>*/}
        {/*  </div>*/}
        {/*)}*/}
        {currency === CurrencyEnum.TON && (
          <Tabs selected={tab} onChange={handleTabChange}>
            <TabsItem value="operations">{t('balance_history')}</TabsItem>
            <TabsItem value="withdrawals">{t('balance_withdrawals')}</TabsItem>
          </Tabs>
        )}
        {renderTabContent()}
      </div>
    );
  }

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      <Header showBackButton>{t('balance_title')}</Header>
      <ScrollView>
        {isCryptoEnabled && (
          <div className="Balance__currencies">
            <SegmentedControl
              options={[
                {
                  label: 'EUR',
                  value: CurrencyEnum.EUR,
                },
                {
                  label: 'TON',
                  value: CurrencyEnum.TON,
                },
                {
                  label: 'PLACE',
                  value: CurrencyEnum.PLACE,
                },
              ]}
              defaultValue={currency}
              onChange={handleChangeCurrency}
            />
          </div>
        )}
        <PullToRefresh isRefreshing={isLoading} onRefresh={handleRefresh}>
          {renderContent()}
        </PullToRefresh>
      </ScrollView>
    </>
  );
};
